import { useRef } from "react";
import * as P from "./style";
import { FeedPosts } from "../../Config/interface.config";
import { isFileVideo } from "../../Config/common.config";
import VideoPlayer from "./VideoHandler";
import { ProtectedImage } from "../ProtectedMedia";
import { ReactComponent as UnlockPostIcon } from "./UnlockPostIcon.svg";

interface Props {
  locked?: boolean;
  post: FeedPosts;
  isSubscribed?: boolean;
}

const PostSlider = ({ locked = false, post, isSubscribed }: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mediaPlaceholder = <P.MediaPlaceholder aria-label="Loading media" />;

  const settings = {
    dots: !locked,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    appendDots: (dots: any) => <ul>{dots}</ul>,
    customPaging: () => <P.Dot className="slider-dot" />,
  };

  /* ---------------- LOCKED POST ---------------- */
  if (locked) {
    const firstFile = post?.files?.[0];
    const lockedMediaUrl = firstFile?.previewURL || undefined;

    return (
      <P.LockedPostContainer ref={containerRef}>
        {lockedMediaUrl ? (
          <ProtectedImage
            as={P.LockedPostImg}
            className="locked-img"
            url={lockedMediaUrl}
            loadingComponent={mediaPlaceholder}
            errorComponent={<P.VideoPlaceholder role="img" aria-label="Media unavailable">Media unavailable</P.VideoPlaceholder>}
          />
        ) : (
          <P.VideoPlaceholder />
        )}
        <P.UnlockPost>
          <UnlockPostIcon />
          <P.UnlockPostBtn className="font-roboto">
            Become a member to unlock
          </P.UnlockPostBtn>
        </P.UnlockPost>
      </P.LockedPostContainer>
    );
  }

  /* ---------------- UNLOCKED POST ---------------- */
  return (
    <div ref={containerRef}>
      {post?.files?.length > 1 ? (
        <P.Slider
          {...settings}
          prevArrow={<P.LeftArrow />}
          nextArrow={<P.NextArrow />}
        >
          {post.files.map((file) => (
            <P.SliderContent key={`file-${file.id}`}>
              {/* IMAGE */}
              {!isFileVideo(file.name) ? (
                <ProtectedImage
                  as={P.PostImage}
                  url={file.fileURL || undefined}
                  loadingComponent={mediaPlaceholder}
                  errorComponent={<P.VideoPlaceholder role="img" aria-label="Media unavailable">Media unavailable</P.VideoPlaceholder>}
                />
              ) : (
                <>
                  {/* VIDEO - lazy loaded */}
                  {file.fileURL ? (
                    <VideoPlayer
                      url={file.fileURL}
                      locked={locked}
                      isSubscribed={isSubscribed}
                      posterUrl={file.previewURL || undefined}
                    />
                  ) : (
                    <P.VideoPlaceholder />
                  )}
                </>
              )}
            </P.SliderContent>
          ))}
        </P.Slider>
      ) : (
        <div>
          {post?.files?.length === 1 && (
            <P.SliderContent>
              {!isFileVideo(post.files[0].name) ? (
                <ProtectedImage
                  as={post.isIntro ? P.IntroPostImage : P.PostImage}
                  url={post.files[0].fileURL || undefined}
                  alt="Post content"
                  loadingComponent={mediaPlaceholder}
                  errorComponent={<P.VideoPlaceholder role="img" aria-label="Media unavailable">Media unavailable</P.VideoPlaceholder>}
                />
              ) : (
                <>
                  {post.files[0].fileURL ? (
                    <VideoPlayer
                      url={post.files[0].fileURL}
                      locked={locked}
                      isSubscribed={isSubscribed}
                      posterUrl={post.files[0].previewURL || undefined}
                    />
                  ) : (
                    <P.VideoPlaceholder />
                  )}
                </>
              )}
            </P.SliderContent>
          )}
        </div>
      )}
    </div>
  );
};

export default PostSlider;
